import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { scheduleApi } from '../../api/schedule'
import { RefusedError } from '../../api/client'
import { useSchedule } from '../../hooks/useSchedule'
import { useSpoke } from '../../hooks/useSpoke'
import { formatDate, formatRelative } from '../../lib/format'
import { jobKindLabel } from '../../lib/jobs'
import type { ScheduleChain, ScheduleJob, ScheduleLive, ScheduleNotifications, ScheduleView } from '../../types/schedule'

// Automatic scans. The answer to "does this run itself, or is someone pressing it?" — the cadence
// the deployment set, when the next pass is owed, and the last run's chain job by job with a link
// to each console. The cadence is read-only on purpose: it is a pack seed field, so the panel names
// the binding an operator would change instead of pretending the browser can.
export function SchedulePanel() {
  const spoke = useSpoke()
  const qc = useQueryClient()
  const q = useSchedule()
  const runNow = useMutation({
    mutationFn: () => scheduleApi.runNow(spoke),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['schedule', spoke] }),
  })

  return (
    <div className="card schedule-card">
      <div className="card-head">
        <div>
          <p className="kicker">Automatic scans</p>
          <h2>{q.isSuccess ? headline(q.data) : 'Automatic scans'}</h2>
          <p className="muted">{q.isSuccess ? blurb(q.data) : 'How often Scout refreshes the pipeline on its own.'}</p>
        </div>
        {q.isSuccess && q.data.canRunNow && (
          <div className="actions">
            <button className="button small" onClick={() => runNow.mutate()} disabled={runNow.isPending || q.data.lastRun?.open}>
              {runNow.isPending ? 'Starting…' : q.data.lastRun?.open ? 'Running…' : 'Run now'}
            </button>
          </div>
        )}
      </div>

      {q.isPending && <p className="muted">Loading…</p>}
      {q.isError && (
        <div className="refusal" role="status">
          <strong>{q.error instanceof RefusedError ? q.error.refusal.reason : 'Unavailable'}</strong>
          <p>{q.error.message}</p>
        </div>
      )}
      {runNow.isError && (
        <div className="refusal" role="status">
          <strong>Not started</strong>
          <p>{runNow.error instanceof RefusedError ? runNow.error.refusal.message : runNow.error.message}</p>
        </div>
      )}

      {q.isSuccess && (
        <>
          <dl className="schedule-facts">
            <div>
              <dt>Cadence</dt>
              <dd>
                {cadenceWords(q.data)}
                <span className="tag binding" title="Set on the deployment; the pack collects it as a seed field.">{q.data.cadenceBinding}</span>
              </dd>
            </div>
            <div>
              <dt>Next run</dt>
              <dd>{q.data.nextDue ? `${formatDate(q.data.nextDue)}, ${timeOf(q.data.nextDue)}` : 'Never — the cadence is off'}</dd>
            </div>
            <div>
              <dt>Last run</dt>
              <dd>
                {q.data.lastRun
                  ? `${formatRelative(q.data.lastRun.startedAt)}${q.data.lastRun.trigger === 'manual' ? ' · started by hand' : ''}`
                  : 'None yet'}
              </dd>
            </div>
          </dl>

          {q.data.live && <LiveLine live={q.data.live} />}

          {q.data.lastRun && q.data.lastRun.outcome && <p className="schedule-outcome">{q.data.lastRun.outcome}</p>}

          {q.data.chain.length === 0 && (
            <p className="muted">
              Nothing to refresh until a mailbox is connected. <Link to="/mailbox">Connect yours</Link> and it joins the next run.
            </p>
          )}

          <ul className="schedule-chain">
            {q.data.chain.map((person) => (
              <ChainRow key={person.userId} person={person} open={q.data.lastRun?.open ?? false} />
            ))}
          </ul>
        </>
      )}
    </div>
  )
}

/** Between the nightly ticks. Two things make mail and calendar live and the line says both, in the order
 *  that matters: Microsoft tells Scout when something moves (seconds), and a poll catches whatever that
 *  missed (minutes). A person who has just sent mail should be able to read when Scout will look, not
 *  guess at it — and when notifications are not working, why. */
function LiveLine({ live }: { live: ScheduleLive }) {
  const notes = live.notifications
  if (live.minutes <= 0 && !notes?.enabled) {
    return (
      <p className="schedule-live off">
        <span className="tag planned">Live sync off</span>
        <span className="muted small">
          New mail and calendar changes land on the next full run, not in between.
          {notes?.why ? ` Notifications are off too: ${lower(notes.why)}` : ''}
        </span>
      </p>
    )
  }
  const parts: string[] = []
  if (notes) parts.push(...notificationWords(notes))
  if (live.mailLastAt) parts.push(`Mail checked ${clock(live.mailLastAt)}`)
  if (live.mailNextAt) parts.push(`next ${clock(live.mailNextAt)}`)
  if (live.calendarLastAt) parts.push(`Calendar checked ${clock(live.calendarLastAt)}`)
  if (live.calendarNextAt) parts.push(`next ${clock(live.calendarNextAt)}`)
  return (
    <p className="schedule-live">
      <span className={`tag ${notes?.enabled ? 'live' : 'planned'}`}>
        {notes?.enabled ? 'Live · notifications on' : live.minutes > 0 ? `Live · every ${live.minutes} min` : 'Live sync off'}
      </span>
      <span className="muted small">
        {parts.length > 0 ? parts.join(' · ') : 'Nothing checked yet; the first pass runs within the next few minutes.'}
      </span>
    </p>
  )
}

/** What the two subscriptions are doing, in the fewest words that are still true. A failure says its first
 *  line, because "something went wrong" sends a person to a log they cannot read. */
function notificationWords(notes: ScheduleNotifications): string[] {
  if (!notes.enabled) return [`Notifications off${notes.why ? ` (${lower(notes.why)})` : ''}`]
  const said: string[] = []
  for (const [label, sub] of [
    ['Mail', notes.mail],
    ['Calendar', notes.calendar],
  ] as const) {
    if (!sub) continue
    if (sub.status === 'active') said.push(`${label.toLowerCase()} subscribed${sub.expiresAt ? ` until ${formatDate(sub.expiresAt)}` : ''}`)
    else if (sub.status === 'reauthorize') said.push(`${label.toLowerCase()} subscription is being renewed`)
    else if (sub.status === 'removed') said.push(`${label.toLowerCase()} subscription was dropped by Microsoft; a new one is on its way`)
    else said.push(`${label.toLowerCase()} subscription failed${sub.lastError ? `: ${sub.lastError}` : ''}`)
    if (sub.lastNotifiedAt) said.push(`last event ${clock(sub.lastNotifiedAt)}`)
  }
  if (said.length === 0) said.push('subscribing to mail and calendar changes now')
  return said
}

/** A reason that reads as the tail of a sentence: "notifications off (the web origin is not https…)". */
function lower(why: string): string {
  const trimmed = why.trim().replace(/\.$/, '')
  return trimmed.charAt(0).toLowerCase() + trimmed.slice(1)
}

function ChainRow({ person, open }: { person: ScheduleChain; open: boolean }) {
  const byKind = new Map(person.jobs.map((j) => [j.kind, j]))
  return (
    <li>
      <div className="schedule-who">
        <strong>{person.displayName}</strong>
        <span className="muted small">{person.address}</span>
        <span className={`tag ${person.complete ? 'live' : person.busy ? 'building' : 'planned'}`}>
          {person.complete ? 'Refreshed' : person.busy ? 'Running' : open ? 'Queued' : 'Waiting for the next run'}
        </span>
      </div>
      <ol className="schedule-steps">
        {person.plan.map((kind) => {
          const job = byKind.get(kind)
          const next = !job && person.nextKind === kind
          return (
            <li key={kind} className={job ? `${job.status.toLowerCase()}${job.warnings > 0 ? ' with-gaps' : ''}` : next ? 'next' : 'pending'}>
              {job ? (
                <Link to={`/jobs/${encodeURIComponent(job.jobId)}`} title={outcomeOf(job)}>
                  {jobKindLabel(kind)}
                </Link>
              ) : (
                <span>{jobKindLabel(kind)}</span>
              )}
              <span className="schedule-step-state">{job ? outcomeOf(job) : next ? 'next' : 'not yet'}</span>
            </li>
          )
        })}
      </ol>
    </li>
  )
}

function outcomeOf(job: ScheduleJob): string {
  if (job.status === 'Completed') return job.warnings > 0 ? 'done, with gaps' : 'done'
  if (job.status === 'Failed') return job.error ? `stopped: ${job.error}` : 'stopped'
  if (job.status === 'Cancelled') return 'cancelled'
  if (job.status === 'Running') return 'running'
  return 'queued'
}

function cadenceWords(view: ScheduleView): string {
  const at = `${String(view.hourUtc).padStart(2, '0')}:00 UTC`
  if (view.cadence === 'off') return 'Off — every scan is pressed by hand'
  return view.cadence === 'weekly' ? `Every Monday at ${at}` : `Every day at ${at}`
}

function headline(view: ScheduleView): string {
  if (view.cadence === 'off') return 'Scout is not refreshing on its own'
  if (view.lastRun?.open) return 'Refreshing your pipeline now'
  return view.cadence === 'weekly' ? 'Scout refreshes your pipeline every week' : 'Scout refreshes your pipeline every night'
}

function blurb(view: ScheduleView): string {
  if (view.cadence === 'off')
    return 'Nothing runs unless someone starts it. Turn the cadence on in the deployment settings to have Scout read new mail, re-weigh contacts and re-read the pipeline on its own.'
  return 'Each run reads your new mail, re-weighs every relationship, re-reads your threads for leads and opportunities, syncs your calendar and finds missing photos — one job at a time, in your name, so you can watch any of them.'
}

/** A wall-clock time, in the reader's own zone: "14:32" reads as a moment, an ISO stamp does not. */
function clock(iso: string): string {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })
}

function timeOf(iso: string): string {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
}
